import {
  GetBuyingPowerResponse,
  NetExposure,
  PnlData,
  SnapshotData,
} from '../../../../apis/statistics/statistics-api.def.ts'
import { useAppSelector } from '../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../features/setting/setting.slice.ts'
import { useIsMobile } from '../../../../common/hooks/use-is-mobile.ts'
import Row from 'antd/es/grid/row'
import IdeyaTypography from '../../../../common/antd/ideya-typography.tsx'
import Col from 'antd/es/grid/col'
import { convertPxToNumber } from '../../../../utils/helpers.ts'
import Switch from 'antd/es/switch/index'
import { IdeaSummaryInfoBox } from './idea-summary-info-box.tsx'
import { currencyValueFormatter } from '../../../../utils/formatters.ts'
import { ValueFormatterParams } from 'ag-grid-enterprise'
import Divider from 'antd/es/divider'
import Spin from 'antd/es/spin/index'
import { selectIdeaListInput } from '../../../../redux/slices/idea-list-input.slice.ts'
import { useTranslation } from 'react-i18next'
import { selectUser } from '../../../../features/user/user.slice.ts'

interface SummaryInfoProps {
  useSnapshotData?: boolean
  buyingPower?: GetBuyingPowerResponse
  netExposure?: NetExposure
  pnlData?: PnlData
  snapshotData?: SnapshotData
  showSummaryInfoSection: boolean
  setShowSummaryInfoSection: () => void
  isBuyingPowerLoading?: boolean
  isNetExposureLoading?: boolean
  isFullyPnlLoading?: boolean
}

export const SummaryInfo: React.FC<SummaryInfoProps> = ({
  useSnapshotData,
  buyingPower,
  netExposure,
  pnlData,
  snapshotData,
  showSummaryInfoSection,
  setShowSummaryInfoSection,
  isBuyingPowerLoading,
  isNetExposureLoading,
  isFullyPnlLoading,
}) => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const isMobile = useIsMobile(768)
  const ideaListInput = useAppSelector(selectIdeaListInput)
  const userInfo = useAppSelector(selectUser)
  const { t } = useTranslation()

  const shouldShowSelectContributorMsg =
    ideaListInput.contributor === '' && !userInfo.bookId

  const usedBuyingPower = useSnapshotData
    ? (snapshotData?.usedBuyingPower ?? buyingPower?.data.usedBuyingPower ?? 0)
    : (buyingPower?.data.usedBuyingPower ?? snapshotData?.usedBuyingPower ?? 0)

  const totalBuyingPower = useSnapshotData
    ? (snapshotData?.buyingPower ?? buyingPower?.data.buyingPower ?? 0)
    : (buyingPower?.data.buyingPower ?? snapshotData?.buyingPower ?? 0)

  const netExposureOnGMV = useSnapshotData
    ? (snapshotData?.netExposureOnGMV ?? netExposure?.netExposureOnGMV ?? 0)
    : (netExposure?.netExposureOnGMV ?? snapshotData?.netExposureOnGMV ?? 0)

  const dailyPnl = useSnapshotData
    ? (snapshotData?.dailyPnl ?? pnlData?.dailyPnl ?? 0)
    : (pnlData?.dailyPnl ?? snapshotData?.dailyPnl ?? 0)

  const wtdPnl = useSnapshotData
    ? (snapshotData?.wtdPnl ?? pnlData?.wtdPnl ?? 0)
    : (pnlData?.wtdPnl ?? snapshotData?.wtdPnl ?? 0)

  const mtdPnl = useSnapshotData
    ? (snapshotData?.mtdPnl ?? pnlData?.mtdPnl ?? 0)
    : (pnlData?.mtdPnl ?? snapshotData?.mtdPnl ?? 0)

  const qtdPnl = useSnapshotData
    ? (snapshotData?.qtdPnl ?? pnlData?.qtdPnl ?? 0)
    : (pnlData?.qtdPnl ?? snapshotData?.qtdPnl ?? 0)

  const ytdPnl = useSnapshotData
    ? (snapshotData?.ytdPnl ?? pnlData?.ytdPnl ?? 0)
    : (pnlData?.ytdPnl ?? snapshotData?.ytdPnl ?? 0)

  const gmv = useSnapshotData
    ? (snapshotData?.gmv ?? netExposure?.gmv ?? 0)
    : (netExposure?.gmv ?? snapshotData?.gmv ?? 0)

  const lmv = useSnapshotData
    ? (snapshotData?.lmv ?? netExposure?.lmv ?? 0)
    : (netExposure?.lmv ?? snapshotData?.lmv ?? 0)

  const smv = useSnapshotData
    ? (snapshotData?.smv ?? netExposure?.smv ?? 0)
    : (netExposure?.smv ?? snapshotData?.smv ?? 0)

  const nmv = useSnapshotData
    ? (snapshotData?.nmv ?? netExposure?.nmv ?? 0)
    : (netExposure?.nmv ?? snapshotData?.nmv ?? 0)

  const pBetaGMv = useSnapshotData
    ? (snapshotData?.pBetaGMv ?? netExposure?.pBetaGMv ?? 0)
    : (netExposure?.pBetaGMv ?? snapshotData?.pBetaGMv ?? 0)

  const pBetaLMv = useSnapshotData
    ? (snapshotData?.pBetaLMv ?? netExposure?.pBetaLMv ?? 0)
    : (netExposure?.pBetaLMv ?? snapshotData?.pBetaLMv ?? 0)

  const pBetaSMv = useSnapshotData
    ? (snapshotData?.pBetaSMv ?? netExposure?.pBetaSMv ?? 0)
    : (netExposure?.pBetaSMv ?? snapshotData?.pBetaSMv ?? 0)

  const pBetaNMv = useSnapshotData
    ? (snapshotData?.pBetaNMv ?? netExposure?.pBetaNMv ?? 0)
    : (netExposure?.pBetaNMv ?? snapshotData?.pBetaNMv ?? 0)

  return (
    <>
      <Row
        justify={'space-between'}
        style={{
          display: 'flex',
          alignItems: 'center',
          alignContent: 'center',
          borderRadius: '8px',
          //border: '1px solid rgba(0, 0, 0, 0.14)',
          border: themeStyle.myTitleBorder,
          padding: '10px 16px',
          background: themeStyle.myTitleBg,
          marginBottom: themeStyle.marginSM,
        }}
      >
        <Col>
          <IdeyaTypography.Text size={'base'}>
            {t('idea_list.summary_info.key_performance')}
          </IdeyaTypography.Text>
        </Col>
        <Row
          gutter={[
            convertPxToNumber(themeStyle.marginXS),
            convertPxToNumber(themeStyle.marginXS),
          ]}
        >
          <Col>
            <IdeyaTypography.Text>
              {t('idea_list.summary_info.show_this_section')}
            </IdeyaTypography.Text>
          </Col>
          <Col>
            <Switch
              checked={showSummaryInfoSection}
              onChange={setShowSummaryInfoSection}
            />
          </Col>
        </Row>
      </Row>
      {showSummaryInfoSection && (
        <>
          <Row
            gutter={[
              convertPxToNumber(themeStyle.marginSM),
              convertPxToNumber(themeStyle.marginSM),
            ]}
            wrap={true}
          >
            <Col
              xs={24}
              sm={24}
              md={8}
              lg={8}
              xl={8}
              style={{ marginBottom: isMobile ? 16 : 0 }}
            >
              <IdeaSummaryInfoBox>
                {shouldShowSelectContributorMsg ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 60,
                      color: '#999',
                    }}
                  >
                    {t('idea_list.summary_info.select_contributor_to_see_data')}
                  </div>
                ) : isBuyingPowerLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 60,
                    }}
                  >
                    <Spin />
                  </div>
                ) : (
                  <>
                    <Row justify={'space-between'}>
                      <Col span={12}>
                        {isMobile ? (
                          <IdeyaTypography.Text>
                            {t('idea_list.summary_info.used_total_bp')}
                          </IdeyaTypography.Text>
                        ) : (
                          <IdeyaTypography.Text>
                            {t(
                              'idea_list.summary_info.used_total_buying_power'
                            )}
                          </IdeyaTypography.Text>
                        )}
                      </Col>
                      <Col span={12} style={{ textAlign: 'end' }}>
                        <IdeyaTypography.Text>
                          $ {(usedBuyingPower / 1000000).toFixed(1)}/{' '}
                          {totalBuyingPower / 1000000}M
                        </IdeyaTypography.Text>
                      </Col>
                    </Row>
                    <Row justify={'space-between'}>
                      <Col span={12}>
                        <IdeyaTypography.Text>
                          {t('idea_list.summary_info.net_exposure_on_gmv')}
                        </IdeyaTypography.Text>
                      </Col>
                      <Col span={12} style={{ textAlign: 'end' }}>
                        <IdeyaTypography.Text>
                          {netExposureOnGMV === 0
                            ? '0'
                            : (netExposureOnGMV * 100).toFixed(2)}
                          %
                        </IdeyaTypography.Text>
                      </Col>
                    </Row>
                  </>
                )}
              </IdeaSummaryInfoBox>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={8}
              lg={8}
              xl={8}
              style={{ marginBottom: isMobile ? 16 : 0 }}
            >
              <IdeaSummaryInfoBox>
                {shouldShowSelectContributorMsg ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 60,
                      color: '#999',
                    }}
                  >
                    {t('idea_list.summary_info.select_contributor_to_see_data')}
                  </div>
                ) : isFullyPnlLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 60,
                    }}
                  >
                    <Spin />
                  </div>
                ) : (
                  <Row gutter={[20, 0]}>
                    <Col span={24} xs={24} sm={24}>
                      <Row justify={'space-between'}>
                        <Col>
                          <IdeyaTypography.Text>Daily PnL</IdeyaTypography.Text>
                        </Col>
                        <Col>
                          <IdeyaTypography.Text>
                            {currencyValueFormatter(
                              {
                                value: dailyPnl,
                              } as ValueFormatterParams,
                              { decimal: 0, currency: 'USD' }
                            )}
                          </IdeyaTypography.Text>
                        </Col>
                      </Row>
                      <Row justify={'space-between'}>
                        <Col>
                          <IdeyaTypography.Text>WTD PnL</IdeyaTypography.Text>
                        </Col>
                        <Col>
                          <IdeyaTypography.Text>
                            {currencyValueFormatter(
                              {
                                value: wtdPnl,
                              } as ValueFormatterParams,
                              { decimal: 0, currency: 'USD' }
                            )}
                          </IdeyaTypography.Text>
                        </Col>
                      </Row>
                    </Col>
                    <Col span={24} xs={24} sm={24}>
                      <Row justify={'space-between'}>
                        <Col>
                          <IdeyaTypography.Text>MTD PnL</IdeyaTypography.Text>
                        </Col>
                        <Col>
                          <IdeyaTypography.Text>
                            {currencyValueFormatter(
                              {
                                value: mtdPnl,
                              } as ValueFormatterParams,
                              { decimal: 0, currency: 'USD' }
                            )}
                          </IdeyaTypography.Text>
                        </Col>
                      </Row>
                      <Row justify={'space-between'}>
                        <Col>
                          <IdeyaTypography.Text>QTD PnL</IdeyaTypography.Text>
                        </Col>
                        <Col>
                          <IdeyaTypography.Text>
                            {currencyValueFormatter(
                              {
                                value: qtdPnl,
                              } as ValueFormatterParams,
                              { decimal: 0, currency: 'USD' }
                            )}
                          </IdeyaTypography.Text>
                        </Col>
                      </Row>
                      <Row justify={'space-between'}>
                        <Col>
                          <IdeyaTypography.Text>YTD PnL</IdeyaTypography.Text>
                        </Col>
                        <Col>
                          <IdeyaTypography.Text>
                            {currencyValueFormatter(
                              {
                                value: ytdPnl,
                              } as ValueFormatterParams,
                              { decimal: 0, currency: 'USD' }
                            )}
                          </IdeyaTypography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                )}
              </IdeaSummaryInfoBox>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={8}
              lg={8}
              xl={8}
              style={{ marginBottom: isMobile ? 16 : 0 }}
            >
              <IdeaSummaryInfoBox>
                {shouldShowSelectContributorMsg ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 60,
                      color: '#999',
                    }}
                  >
                    {t('idea_list.summary_info.select_contributor_to_see_data')}
                  </div>
                ) : isNetExposureLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 60,
                    }}
                  >
                    <Spin />
                  </div>
                ) : (
                  <>
                    <Row>
                      <Col span={4} xs={4} sm={4}>
                        <IdeyaTypography.Text>
                          {t('idea_list.summary_info.metric')}
                        </IdeyaTypography.Text>
                      </Col>
                      <Col
                        span={10}
                        xs={10}
                        sm={10}
                        style={{ textAlign: 'end' }}
                      >
                        <IdeyaTypography.Text
                          style={{
                            textAlign: 'end',
                          }}
                        >
                          {t('idea_list.summary_info.market_value')}
                        </IdeyaTypography.Text>
                      </Col>
                      <Col
                        span={10}
                        xs={10}
                        sm={10}
                        style={{ textAlign: 'end' }}
                      >
                        <IdeyaTypography.Text
                          style={{
                            textAlign: 'end',
                          }}
                        >
                          {t('idea_list.summary_info.beta_adjusted')}
                        </IdeyaTypography.Text>
                      </Col>
                    </Row>
                    <Divider style={{ margin: '4px 0px' }} />
                    <Row>
                      <Col span={4} xs={4} sm={4}>
                        <IdeyaTypography.Text>GMV</IdeyaTypography.Text>
                      </Col>
                      <Col
                        span={10}
                        xs={10}
                        sm={10}
                        style={{ textAlign: 'end' }}
                      >
                        <IdeyaTypography.Text style={{ textAlign: 'end' }}>
                          {currencyValueFormatter(
                            { value: gmv } as ValueFormatterParams,
                            { decimal: 0, currency: 'USD' }
                          )}
                        </IdeyaTypography.Text>
                      </Col>
                      <Col
                        span={10}
                        xs={10}
                        sm={10}
                        style={{ textAlign: 'end' }}
                      >
                        <IdeyaTypography.Text style={{ textAlign: 'end' }}>
                          {currencyValueFormatter(
                            {
                              value: pBetaGMv,
                            } as ValueFormatterParams,
                            { decimal: 0, currency: 'USD' }
                          )}
                        </IdeyaTypography.Text>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={4} xs={4} sm={4}>
                        <IdeyaTypography.Text>LMV</IdeyaTypography.Text>
                      </Col>
                      <Col
                        span={10}
                        xs={10}
                        sm={10}
                        style={{ textAlign: 'end' }}
                      >
                        <IdeyaTypography.Text style={{ textAlign: 'end' }}>
                          {currencyValueFormatter(
                            { value: lmv } as ValueFormatterParams,
                            { decimal: 0, currency: 'USD' }
                          )}
                        </IdeyaTypography.Text>
                      </Col>
                      <Col
                        span={10}
                        xs={10}
                        sm={10}
                        style={{ textAlign: 'end' }}
                      >
                        <IdeyaTypography.Text style={{ textAlign: 'end' }}>
                          {currencyValueFormatter(
                            {
                              value: pBetaLMv,
                            } as ValueFormatterParams,
                            { decimal: 0, currency: 'USD' }
                          )}
                        </IdeyaTypography.Text>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={4} xs={4} sm={4}>
                        <IdeyaTypography.Text>SMV</IdeyaTypography.Text>
                      </Col>
                      <Col
                        span={10}
                        xs={10}
                        sm={10}
                        style={{ textAlign: 'end' }}
                      >
                        <IdeyaTypography.Text style={{ textAlign: 'end' }}>
                          {currencyValueFormatter(
                            { value: smv } as ValueFormatterParams,
                            { decimal: 0, currency: 'USD' }
                          )}
                        </IdeyaTypography.Text>
                      </Col>
                      <Col
                        span={10}
                        xs={10}
                        sm={10}
                        style={{ textAlign: 'end' }}
                      >
                        <IdeyaTypography.Text style={{ textAlign: 'end' }}>
                          {currencyValueFormatter(
                            {
                              value: pBetaSMv,
                            } as ValueFormatterParams,
                            { decimal: 0, currency: 'USD' }
                          )}
                        </IdeyaTypography.Text>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={4} xs={4} sm={4}>
                        <IdeyaTypography.Text>NMV</IdeyaTypography.Text>
                      </Col>
                      <Col
                        span={10}
                        xs={10}
                        sm={10}
                        style={{ textAlign: 'end' }}
                      >
                        <IdeyaTypography.Text style={{ textAlign: 'end' }}>
                          {currencyValueFormatter(
                            { value: nmv } as ValueFormatterParams,
                            { decimal: 0, currency: 'USD' }
                          )}
                        </IdeyaTypography.Text>
                      </Col>
                      <Col
                        span={10}
                        xs={10}
                        sm={10}
                        style={{ textAlign: 'end' }}
                      >
                        <IdeyaTypography.Text style={{ textAlign: 'end' }}>
                          {currencyValueFormatter(
                            {
                              value: pBetaNMv,
                            } as ValueFormatterParams,
                            { decimal: 0, currency: 'USD' }
                          )}
                        </IdeyaTypography.Text>
                      </Col>
                    </Row>
                  </>
                )}
              </IdeaSummaryInfoBox>
            </Col>
          </Row>
        </>
      )}
    </>
  )
}
